import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const DailyMediaDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/admin/api/daily-media.php');
        const data = await res.json();
        const arr = Array.isArray(data) ? data : (data.data || []);
        const found = arr.find(item => String(item.id) === String(id));
        if (found) setMedia(found);
        else setError('Media not found');
      } catch {
        setError('Failed to load media details');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <div className="spinner" style={{ width: 48, height: 48, borderWidth: 4 }} />
        <p className="text-gray-400 font-inter text-sm">Loading media...</p>
      </div>
    );
  }

  if (error || !media) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-photo-video text-accent text-3xl" />
          </div>
          <h3 className="font-poppins font-800 text-navy text-2xl mb-3">Media Not Found</h3>
          <p className="text-gray-400 font-inter text-sm mb-8">{error || 'This media item could not be found.'}</p>
          <button onClick={() => navigate('/daily-media')}
            className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white font-poppins font-600 text-sm rounded-xl hover:bg-primary-dark transition-colors mx-auto">
            <i className="fas fa-arrow-left" /> Back to Daily Media
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <Helmet>
        <title>{media.title} — PAK School Daily Media</title>
        <meta name="description" content={media.description || `${media.title} — PAK School & College System`} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 sticky top-[70px] z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 text-sm font-inter">
          <button onClick={() => navigate('/daily-media')} className="text-gray-400 hover:text-primary transition-colors flex items-center gap-1.5">
            <i className="fas fa-arrow-left text-xs" /> Daily Media
          </button>
          <i className="fas fa-chevron-right text-gray-300 text-[10px]" />
          <span className="text-navy font-500 truncate">{media.title}</span>
        </div>
      </div>

      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Media */}
            <div className="lg:col-span-2 space-y-5">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="relative">
                  {media.media_type === 'image' ? (
                    <img src={`admin/${media.file_path}`} alt={media.title}
                      className="w-full object-contain max-h-[500px]" />
                  ) : (
                    <div className="bg-gray-900">
                      <video src={`admin/${media.file_path}`} className="w-full max-h-[500px]" controls />
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className={`text-xs font-poppins font-700 px-2.5 py-1.5 rounded-full ${media.media_type === 'image' ? 'bg-primary text-white' : 'bg-accent text-white'}`}>
                      <i className={`fas ${media.media_type === 'image' ? 'fa-camera' : 'fa-video'} mr-1.5`} />
                      {media.media_type}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description */}
              {media.description && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="font-poppins font-700 text-navy text-base mb-3">Description</h3>
                  <p className="text-gray-500 font-inter text-sm leading-relaxed">{media.description}</p>
                </div>
              )}
            </div>

            {/* Info sidebar */}
            <div className="space-y-5">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-[100px]">
                <h3 className="font-poppins font-700 text-navy text-base mb-5 pb-3 border-b border-gray-100">Media Info</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-400 font-inter mb-1">Title</p>
                    <p className="font-poppins font-600 text-navy text-sm">{media.title}</p>
                  </div>
                  {media.category && (
                    <div>
                      <p className="text-xs text-gray-400 font-inter mb-1">Category</p>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-primary rounded-full text-xs font-poppins font-600">
                        <i className="fas fa-tag text-[9px]" />{media.category}
                      </span>
                    </div>
                  )}
                  {media.date && (
                    <div>
                      <p className="text-xs text-gray-400 font-inter mb-1">Date</p>
                      <p className="font-inter font-500 text-navy text-sm">{fmtDate(media.date)}</p>
                    </div>
                  )}
                  {media.location && (
                    <div>
                      <p className="text-xs text-gray-400 font-inter mb-1">Location</p>
                      <p className="font-inter text-navy text-sm flex items-center gap-1.5">
                        <i className="fas fa-map-marker-alt text-accent text-xs" />{media.location}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-5 border-t border-gray-100 space-y-3">
                  <a href={`admin/${media.file_path}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-primary text-white text-sm font-poppins font-600 rounded-xl hover:bg-primary-dark transition-colors">
                    <i className="fas fa-external-link-alt" /> View Full Size
                  </a>
                  <button onClick={() => navigate('/daily-media')}
                    className="flex items-center justify-center gap-2 w-full py-2.5 bg-gray-100 text-gray-600 text-sm font-poppins font-600 rounded-xl hover:bg-gray-200 transition-colors">
                    <i className="fas fa-th" /> Browse All Media
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DailyMediaDetails;
